/* Dream Collection scroll reveal — fade-up only, no parallax (luxury = stillness) */
(function () {
  'use strict';

  if (!document.body.classList.contains('is-dream')) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

  document.querySelectorAll('.drm-reveal').forEach(function (el) {
    observer.observe(el);
  });
})();

/* =============================================================================
   DREAM — Inline-style enforcement for search input + filter sidebar.

   Why JS instead of CSS: Impact theme uses runtime CSS variables that get set
   late via JS color schemes + Web Components with shadow DOM. CSS overrides
   keep losing the specificity / load-order race. Inline styles applied via
   element.style.setProperty('color', '#000', 'important') win unconditionally
   against any external CSS — even other !important rules.
   ============================================================================= */
(function () {
  'use strict';

  if (!document.body.classList.contains('is-dream')) return;

  // Brand colors
  var INK   = '#1A1A1A';   // body text
  var INK0  = '#000000';   // pure black
  var GOLD  = '#B8964A';
  var GOLD_DEEP = '#8E6F2E';
  var MUTE  = '#6B6660';
  var BG    = '#FFFFFF';

  function force(el, prop, val) {
    if (el && el.style) { el.style.setProperty(prop, val, 'important'); }
  }

  // ------- Search inputs (header drawer + predictive search) -------
  function fixSearch(root) {
    var scope = root || document;
    // Every potential search input
    scope.querySelectorAll(
      'input[type="search"], input[name="q"], .search-input > input, ' +
      'search-drawer input, .search-drawer input, predictive-search input'
    ).forEach(function (input) {
      force(input, 'color', INK0);
      force(input, '-webkit-text-fill-color', INK0);
      force(input, 'background', BG);
      force(input, 'background-color', BG);
      force(input, 'caret-color', GOLD);
      force(input, 'font-family', "'Inter', system-ui, sans-serif");
    });

    // The drawer host itself — set the CSS vars Impact reads + the color
    scope.querySelectorAll(
      'search-drawer, .search-drawer, .search-input, predictive-search, ' +
      '.predictive-search'
    ).forEach(function (host) {
      force(host, '--text-color',                    '0, 0, 0');
      force(host, '--text-primary',                  '0, 0, 0');
      force(host, '--dialog-text-color',             '0, 0, 0');
      force(host, '--header-text-color',             '0, 0, 0');
      force(host, '--header-transparent-text-color', '0, 0, 0');
      force(host, 'color', INK0);
    });

    // Search drawer "Clear" / close labels
    scope.querySelectorAll(
      'search-drawer .text-subdued, .search-drawer .text-subdued, ' +
      'search-drawer button, .search-drawer button'
    ).forEach(function (btn) {
      force(btn, 'color', INK);
    });

    // Header search icon link (the "Search" affordance in main menu)
    scope.querySelectorAll(
      '.header__main-nav a[aria-controls="search-drawer"], ' +
      '.header__icon-list a[href*="/search"], ' +
      '.header__search, .header__search *'
    ).forEach(function (el) {
      force(el, 'color', INK);
      // make any inline SVGs visible
      if (el.tagName === 'svg' || el.querySelector) {
        el.querySelectorAll && el.querySelectorAll('svg').forEach(function (svg) {
          force(svg, 'color', INK);
          force(svg, 'fill',  INK);
          force(svg, 'stroke', INK);
        });
      }
    });
  }

  // ------- Collection filter sidebar (facets-vertical) -------
  // Collection page sits on a DARK backdrop, so filter text needs WHITE +
  // gold accents (not dark ink). User explicitly asked for "white and gold
  // text" — labels get white, group titles get gold, counts dimmed white.
  function fixFilters(root) {
    var scope = root || document;
    var WHITE       = '#FFFFFF';
    var WHITE_DIM   = 'rgba(255, 255, 255, 0.65)';
    var WHITE_FAINT = 'rgba(255, 255, 255, 0.45)';

    // Container + every descendant — strip backgrounds, neutralize colors
    scope.querySelectorAll(
      'facet-form, #facet-form, form[is="facet-form"], ' +
      '.facets-vertical, .facets-sidebar, .collection-filters, ' +
      '.filter-and-sort'
    ).forEach(function (host) {
      force(host, 'background', 'transparent');
      force(host, 'background-color', 'transparent');
      force(host, 'color', WHITE);
      // Override Impact's --text-color so any rgb(var(--text-color)) reads white
      force(host, '--text-color', '255, 255, 255');
      force(host, '--text-primary', '255, 255, 255');
      force(host, '--background', '0, 0, 0');
      force(host, 'border', 'none');
      force(host, 'box-shadow', 'none');

      // Sweep every descendant: kill background, force white text
      host.querySelectorAll('*').forEach(function (el) {
        // Checkbox/radio inputs get gold accent only — don't touch their color
        if (el.matches && el.matches('input[type="checkbox"], input[type="radio"]')) {
          force(el, 'accent-color', GOLD);
          return;
        }
        // Strip backgrounds so nothing forms a box
        force(el, 'background-color', 'transparent');
        // Default everything to white text (specific selectors below refine)
        force(el, 'color', WHITE);
      });

      // Section title labels (filter group names) — GOLD uppercase eyebrow
      host.querySelectorAll(
        '.accordion__toggle, .accordion__toggle .bold, summary.bold, ' +
        'summary, .bold, .h5, .h6'
      ).forEach(function (lbl) {
        force(lbl, 'color', GOLD);
        force(lbl, 'font-family', "'Inter', system-ui, sans-serif");
        force(lbl, 'font-weight', '500');
        force(lbl, 'letter-spacing', '0.08em');
        force(lbl, 'text-transform', 'uppercase');
        force(lbl, 'font-size', '0.75rem');
      });

      // Filter value labels — WHITE, sentence case
      host.querySelectorAll(
        'label, .checkbox-list label, .checkbox-list > *, ' +
        '.checkbox + span, input + span, span'
      ).forEach(function (lbl) {
        // Don't recolor labels inside the gold heading area
        if (lbl.closest && lbl.closest('.accordion__toggle, summary')) return;
        force(lbl, 'color', WHITE);
        force(lbl, 'font-family', "'Inter', system-ui, sans-serif");
        force(lbl, 'font-weight', '400');
        force(lbl, 'text-transform', 'none');
        force(lbl, 'letter-spacing', '0');
        force(lbl, 'font-size', '0.9rem');
      });

      // Anchors inside the filter list (sometimes filters are <a> not <label>)
      host.querySelectorAll('a').forEach(function (a) {
        if (a.closest && a.closest('.accordion__toggle, summary')) return;
        if (a.matches && a.matches('.active-facet, .facet-tag, a[is="facet-remove"]')) return;
        force(a, 'color', WHITE);
      });

      // Count tags + muted text — slightly dimmed white
      host.querySelectorAll('.count, .text-subdued, .price-range__divider').forEach(function (el) {
        force(el, 'color', WHITE_DIM);
      });

      // SVG arrows / icons — white
      host.querySelectorAll('svg').forEach(function (svg) {
        force(svg, 'color',  WHITE);
        force(svg, 'fill',   WHITE);
        force(svg, 'stroke', WHITE);
      });

      // Active filter pills — gold-pale fill + gold-deep text on the dark bg
      host.querySelectorAll(
        '.active-facets a, .active-facet, .facet-tag, a[is="facet-remove"]'
      ).forEach(function (pill) {
        force(pill, 'color', GOLD);
        force(pill, 'background-color', 'rgba(184, 150, 74, 0.15)');
        force(pill, 'border', '1px solid rgba(184, 150, 74, 0.45)');
      });

      // Number inputs (price range) — white text on subtle white-tinted bg
      host.querySelectorAll('input[type="number"], input[type="text"]').forEach(function (input) {
        force(input, 'color', WHITE);
        force(input, '-webkit-text-fill-color', WHITE);
        force(input, 'background-color', 'rgba(255, 255, 255, 0.06)');
        force(input, 'border', '1px solid rgba(255, 255, 255, 0.20)');
        force(input, 'caret-color', GOLD);
      });

      // Accordion divider lines — gold-tinted hairline
      host.querySelectorAll('.accordion').forEach(function (acc) {
        force(acc, 'border-color', 'rgba(184, 150, 74, 0.25)');
        force(acc, 'border-bottom-color', 'rgba(184, 150, 74, 0.25)');
      });
    });
  }

  function fixAll(root) {
    fixSearch(root);
    fixFilters(root);
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { fixAll(); });
  } else {
    fixAll();
  }

  // Re-apply when search drawer opens or facets refresh.
  // Watch the whole body for added nodes — covers drawer-open animation,
  // section-rendering API updates, and AJAX-paginated collection results.
  var mo = new MutationObserver(function (mutations) {
    var needsSearch = false;
    var needsFilters = false;
    mutations.forEach(function (m) {
      m.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        // Search-related?
        if (node.matches && (
          node.matches('search-drawer, .search-drawer, .search-input, predictive-search, input[type="search"]') ||
          node.querySelector && node.querySelector('search-drawer, .search-drawer, .search-input, input[type="search"]')
        )) needsSearch = true;
        // Filter-related?
        if (node.matches && (
          node.matches('facet-form, .facets-vertical, .accordion') ||
          node.querySelector && node.querySelector('facet-form, .facets-vertical')
        )) needsFilters = true;
      });
    });
    if (needsSearch) fixSearch();
    if (needsFilters) fixFilters();
  });
  mo.observe(document.body, { childList: true, subtree: true });

  // Also re-run on common Impact theme events (drawer open, section reload)
  ['drawer:open', 'predictive-search:open', 'section:reload',
   'shopify:section:load', 'shopify:section:select'].forEach(function (evt) {
    document.addEventListener(evt, function () {
      setTimeout(fixAll, 50);
    }, true);
  });

  // Final safety net — re-run after a short delay (catches anything that
  // initialized after our DOMContentLoaded run)
  setTimeout(fixAll, 200);
  setTimeout(fixAll, 800);
})();
